import { NoteLevelBadge } from '@/components/notes/note-level-badge';
import { authorName, formatNoteDate, formatNoteDateTime } from '@/lib/note-format';
import { cn } from '@/lib/utils';
import type { NoteThread } from '@mss_creator/shared';
import { Icon } from '@pms-digital/design-system';
import { targetTypeWord } from './notes-view-logic';
import type { NotesSort, NotesSortDirection } from './notes-view-search';

export interface NotesViewTableProps {
  threads: NoteThread[];
  sort: NotesSort;
  dir: NotesSortDirection;
  /** Clicking a sortable header: the page switches the criterion, or inverts it when it already is the one. */
  onSort: (sort: NotesSort) => void;
  /** The discussion open in the panel beside the table. */
  selectedId: string | null;
  onSelect: (threadId: string) => void;
  /** The check of a row: resolves an open discussion, reopens a resolved one. */
  onToggleResolved: (thread: NoteThread) => void;
  /** The clock of the relative dates; tests pin it. */
  now?: Date;
}

function SortableHeader({
  label,
  sort,
  dir,
  active,
  onSort,
}: {
  label: string;
  sort: NotesSort;
  dir: NotesSortDirection;
  active: boolean;
  onSort: (sort: NotesSort) => void;
}) {
  return (
    <th
      scope="col"
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className="whitespace-nowrap px-3 py-2 text-left font-semibold"
    >
      <button
        type="button"
        onClick={() => onSort(sort)}
        className="inline-flex items-center gap-1 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
      >
        {label}
        {active &&
          (dir === 'asc' ? (
            <Icon name="arrow_upward" size={14} />
          ) : (
            <Icon name="arrow_downward" size={14} />
          ))}
      </button>
    </th>
  );
}

/**
 * The discussions as a dense table (planche 12): Cible, Note, Auteur, Niveau,
 * Créée le, Réponses, and the check that resolves. The four headers that sort
 * move « Trier par » with them; a row opens its discussion in the panel beside
 * the table.
 */
export function NotesViewTable({
  threads,
  sort,
  dir,
  onSort,
  selectedId,
  onSelect,
  onToggleResolved,
  now,
}: NotesViewTableProps) {
  return (
    <div className="min-w-0 flex-1 overflow-hidden rounded-lg border border-gray-200 bg-white">
      <table className="w-full border-collapse text-caption">
        <thead className="border-gray-200 border-b bg-gray-50 text-caption text-gray-600">
          <tr>
            <SortableHeader
              label="Cible"
              sort="grille"
              dir={dir}
              active={sort === 'grille'}
              onSort={onSort}
            />
            <th scope="col" className="px-3 py-2 text-left font-semibold">
              Note
            </th>
            <SortableHeader
              label="Auteur"
              sort="auteur"
              dir={dir}
              active={sort === 'auteur'}
              onSort={onSort}
            />
            <SortableHeader
              label="Niveau"
              sort="niveau"
              dir={dir}
              active={sort === 'niveau'}
              onSort={onSort}
            />
            <SortableHeader
              label="Créée le"
              sort="recentes"
              dir={dir}
              active={sort === 'recentes'}
              onSort={onSort}
            />
            <th scope="col" className="px-3 py-2 text-left font-semibold">
              Réponses
            </th>
            <th scope="col" className="w-10 px-3 py-2">
              <span className="sr-only">Résoudre</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {threads.map((thread) => {
            const { typeWord, label } = targetTypeWord(thread);
            const resolved = thread.resolvedAt !== null;
            const selected = thread.id === selectedId;
            return (
              // biome-ignore lint/a11y/useKeyWithClickEvents: the keyboard opens the discussion through the button of the Note cell.
              <tr
                key={thread.id}
                aria-selected={selected}
                onClick={() => onSelect(thread.id)}
                className={cn(
                  'cursor-pointer border-gray-100 border-b last:border-b-0 hover:bg-gray-50',
                  selected && 'bg-bleu-nuit-tint hover:bg-bleu-nuit-tint',
                )}
              >
                <td className="px-3 py-2 align-middle">
                  <span className="block text-gray-500 text-micro">{typeWord}</span>
                  <span className="block font-semibold text-bleu-nuit">{label}</span>
                </td>
                <td className="max-w-[320px] px-3 py-2 align-middle">
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onSelect(thread.id);
                    }}
                    aria-label={`Ouvrir la discussion sur ${thread.targetLabel}`}
                    className="line-clamp-2 w-full whitespace-pre-line break-words rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
                  >
                    {thread.body}
                  </button>
                </td>
                <td className="px-3 py-2 align-middle text-gray-700">
                  {authorName(thread.author)}
                </td>
                <td className="px-3 py-2 align-middle">
                  <NoteLevelBadge visibility={thread.visibility} />
                </td>
                <td className="whitespace-nowrap px-3 py-2 align-middle text-gray-700">
                  <time dateTime={thread.createdAt} title={formatNoteDateTime(thread.createdAt)}>
                    {formatNoteDate(thread.createdAt, now)}
                  </time>
                </td>
                <td className="px-3 py-2 align-middle text-gray-700">
                  {thread.replies.length > 0 ? thread.replies.length : '-'}
                </td>
                <td className="px-3 py-2 text-right align-middle">
                  <button
                    type="button"
                    aria-label={`${resolved ? 'Rouvrir' : 'Résoudre'} la discussion sur ${thread.targetLabel}`}
                    title={resolved ? 'Rouvrir' : 'Résoudre'}
                    onClick={(event) => {
                      event.stopPropagation();
                      onToggleResolved(thread);
                    }}
                    className="inline-flex size-7 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 hover:text-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
                  >
                    {resolved ? <Icon name="undo" size={16} /> : <Icon name="check" size={16} />}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
